import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Clock, Euro, Home, AlertTriangle, CheckCircle, Edit, Trash2, Plus } from 'lucide-react';
import { masterSupabase } from "@/lib/supabase-master.js";

// UUID validation helper
const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * MaintenanceCalendarWidget - Connected to Dropdowns
 * 
 * Features:
 * - Receives selectedOwner and selectedProperty from parent
 * - Filters maintenance data based on dropdown selections
 * - Direct Supabase integration (Dashboard approach)
 * - Real-time data filtering
 * - Blau-grau-silberne Marmorierung für cooles Design
 */
const MaintenanceCalendarWidget = ({ selectedOwner, selectedProperty, onCreateMaintenance }) => {
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [statistics, setStatistics] = useState({
    upcoming: 0,
    overdue: 0,
    totalEstimatedCost: 0,
    affectedProperties: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load maintenance tasks when selectedOwner or selectedProperty changes
  useEffect(() => {
    loadMaintenanceTasks();
    loadStatistics();
  }, [selectedOwner, selectedProperty]);

  /**
   * Load maintenance tasks with filtering using Dashboard approach
   */
  const loadMaintenanceTasks = async () => {
    try {
      setLoading(true);
      console.log('🔍 MaintenanceCalendarWidget: Loading tasks for owner:', selectedOwner, 'property:', selectedProperty);
      
      // DASHBOARD APPROACH: Direct masterSupabase access
      let maintenanceQuery = masterSupabase
        .from('maintenance')
        .select(`
          *,
          properties!maintenance_property_id_fkey (
            id,
            property_name,
            street,
            owner_entity
          ),
          units!maintenance_unit_id_fkey (
            id,
            unit_identifier
          )
        `)
        .order('scheduled_date', { ascending: true });
      
      // Apply filters based on dropdown selections
      if (selectedProperty && selectedProperty !== 'all' && isValidUUID(selectedProperty)) {
        // Filter by specific property
        maintenanceQuery = maintenanceQuery.eq('property_id', selectedProperty);
        console.log('🔍 Filtering by property:', selectedProperty);
      } else if (selectedOwner && selectedOwner !== 'all' && isValidUUID(selectedOwner)) {
        // Filter by owner - need to join with properties
        const { data: ownerProperties } = await masterSupabase
          .from('properties')
          .select('id')
          .eq('owner_entity', selectedOwner);
        
        if (ownerProperties && ownerProperties.length > 0) {
          const propertyIds = ownerProperties.map(p => p.id);
          maintenanceQuery = maintenanceQuery.in('property_id', propertyIds);
          console.log('🔍 Filtering by owner properties:', propertyIds.length);
        } else {
          console.log('🔍 No properties found for owner, returning empty results');
          setMaintenanceTasks([]);
          setLoading(false);
          return;
        }
      }
      
      const { data: tasksData, error } = await maintenanceQuery;
      
      if (error) {
        console.error('❌ MaintenanceCalendarWidget: Error loading tasks:', error);
        setError('Fehler beim Laden der Wartungsaufgaben');
        setMaintenanceTasks([]);
        return;
      }
      
      console.log('✅ MaintenanceCalendarWidget: Tasks loaded:', tasksData?.length || 0);
      
      // Process and enrich task data
      const processedTasks = (tasksData || []).map(task => ({
        ...task,
        address: task.properties?.street || task.properties?.property_name || 'Unbekannte Adresse',
        property_name: task.properties?.property_name || 'Unbekanntes Objekt'
      }));
      
      setMaintenanceTasks(processedTasks);
      
    } catch (error) {
      console.error('❌ MaintenanceCalendarWidget: Error loading tasks (catch):', error);
      setError('Fehler beim Laden der Wartungsaufgaben');
      setMaintenanceTasks([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load statistics with filtering
   */
  const loadStatistics = async () => {
    try {
      console.log('🔍 MaintenanceCalendarWidget: Loading statistics...');
      
      const currentDate = new Date().toISOString().split('T')[0];
      
      // Base query for statistics
      let statsQuery = masterSupabase
        .from('maintenance')
        .select('scheduled_date, status, estimated_cost, property_id');
      
      // Apply same filters as main query
      if (selectedProperty && selectedProperty !== 'all' && isValidUUID(selectedProperty)) {
        statsQuery = statsQuery.eq('property_id', selectedProperty);
      } else if (selectedOwner && selectedOwner !== 'all' && isValidUUID(selectedOwner)) {
        const { data: ownerProperties } = await masterSupabase
          .from('properties')
          .select('id')
          .eq('owner_entity', selectedOwner);
        
        if (ownerProperties && ownerProperties.length > 0) {
          const propertyIds = ownerProperties.map(p => p.id);
          statsQuery = statsQuery.in('property_id', propertyIds);
        } else {
          setStatistics({ upcoming: 0, overdue: 0, totalEstimatedCost: 0, affectedProperties: 0 });
          return;
        }
      }
      
      const { data: statsData, error } = await statsQuery;
      
      if (error) {
        console.error('❌ Error loading statistics:', error);
        return;
      }
      
      // Calculate statistics
      const stats = {
        upcoming: 0,
        overdue: 0,
        totalEstimatedCost: 0,
        affectedProperties: new Set()
      };
      
      (statsData || []).forEach(task => {
        const taskDate = new Date(task.scheduled_date);
        const today = new Date();
        
        if (task.status !== 'abgeschlossen') {
          if (taskDate < today) {
            stats.overdue++;
          } else {
            stats.upcoming++;
          }
        }
        
        stats.totalEstimatedCost += parseFloat(task.estimated_cost || 0);
        stats.affectedProperties.add(task.property_id);
      });
      
      setStatistics({
        ...stats,
        affectedProperties: stats.affectedProperties.size
      });
      
      console.log('✅ Statistics loaded:', stats);
      
    } catch (error) {
      console.error('❌ Error loading statistics (catch):', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      console.log('🔄 Completing task:', taskId);
      
      const { error } = await masterSupabase
        .from('maintenance')
        .update({ 
          status: 'abgeschlossen',
          completed_date: new Date().toISOString()
        })
        .eq('id', taskId);
      
      if (error) {
        console.error('❌ Error completing task:', error);
        setError('Fehler beim Abschließen der Wartung');
        return;
      }
      
      console.log('✅ Task completed successfully');
      await loadMaintenanceTasks();
      await loadStatistics();
      
    } catch (error) {
      console.error('❌ Error completing task (catch):', error);
      setError('Fehler beim Abschließen der Wartung');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diese Wartung löschen möchten?')) {
      try {
        console.log('🗑️ Deleting task:', taskId);
        
        const { error } = await masterSupabase
          .from('maintenance')
          .delete()
          .eq('id', taskId);
        
        if (error) {
          console.error('❌ Error deleting task:', error);
          setError('Fehler beim Löschen der Wartung');
          return;
        }
        
        console.log('✅ Task deleted successfully');
        await loadMaintenanceTasks();
        await loadStatistics();
        
      } catch (error) {
        console.error('❌ Error deleting task (catch):', error);
        setError('Fehler beim Löschen der Wartung');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Morgen';
    if (diffDays === -1) return 'Gestern';
    if (diffDays > 0) return `In ${diffDays} Tagen`;
    return `${Math.abs(diffDays)} Tage überfällig`;
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'hoch':
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'mittel':
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'niedrig':
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const formatPriority = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'hoch':
      case 'high':
        return 'Hoch';
      case 'mittel':
      case 'medium':
        return 'Mittel';
      case 'niedrig':
      case 'low':
        return 'Niedrig';
      default:
        return priority || 'Unbekannt';
    }
  };

  // Separate tasks into overdue and upcoming
  const currentDate = new Date();
  const overdueTasks = maintenanceTasks.filter(task => 
    new Date(task.scheduled_date) < currentDate && task.status !== 'abgeschlossen'
  );
  const upcomingTasks = maintenanceTasks.filter(task => 
    new Date(task.scheduled_date) >= currentDate && task.status !== 'abgeschlossen'
  ).slice(0, 5); // Show only next 5 upcoming tasks

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Wartungskalender
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Wartungskalender
            {(selectedOwner !== 'all' || selectedProperty !== 'all') && (
              <Badge variant="outline" className="ml-2">
                Gefiltert
              </Badge>
            )}
          </CardTitle>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-800 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}
        
        {/* Summary Stats with Blau-Grau-Silber Marmorierung */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-lg p-4 bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200 dark:from-slate-800 dark:via-blue-900/30 dark:to-slate-700 border border-slate-200 dark:border-slate-600 shadow-md">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Anstehend</span>
            </div>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">
              {statistics.upcoming}
            </p>
          </div>
          
          <div className="rounded-lg p-4 bg-gradient-to-br from-red-50 via-slate-100 to-red-100 dark:from-red-900/20 dark:via-slate-800 dark:to-red-900/30 border border-slate-200 dark:border-slate-600 shadow-md">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Überfällig</span>
            </div>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">
              {statistics.overdue}
            </p>
          </div>
          
          <div className="rounded-lg p-4 bg-gradient-to-br from-emerald-50 via-slate-100 to-emerald-100 dark:from-emerald-900/20 dark:via-slate-800 dark:to-emerald-900/30 border border-slate-200 dark:border-slate-600 shadow-md">
            <div className="flex items-center gap-2">
              <Euro className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Geschätzte Kosten</span>
            </div>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">
              {formatCurrency(statistics.totalEstimatedCost)}
            </p>
          </div>
          
          <div className="rounded-lg p-4 bg-gradient-to-br from-purple-50 via-slate-100 to-purple-100 dark:from-purple-900/20 dark:via-slate-800 dark:to-purple-900/30 border border-slate-200 dark:border-slate-600 shadow-md">
            <div className="flex items-center gap-2">
              <Home className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Betroffene Objekte</span>
            </div>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">
              {statistics.affectedProperties}
            </p>
          </div>
        </div>

        {/* Overdue Tasks */}
        {overdueTasks.length > 0 && (
          <div>
            <h4 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Überfällige Wartungen ({overdueTasks.length})
            </h4>
            <div className="space-y-3">
              {overdueTasks.map((task) => (
                <div key={task.id} className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-gradient-to-r from-red-50 via-slate-50 to-red-50 dark:from-red-900/10 dark:via-slate-800/50 dark:to-red-900/10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getPriorityColor(task.priority)}>
                          {formatPriority(task.priority)}
                        </Badge>
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                          {getRelativeDate(task.scheduled_date)}
                        </Badge>
                      </div>
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        {task.title || task.description || 'Wartungsaufgabe'}
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {task.address}
                        {task.units && ` • Einheit ${task.unit_info?.unit_identifier}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {task.contractor_name || 'Kein Auftragnehmer'} • {task.maintenance_type || 'Allgemeine Wartung'}
                      </p>
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(task.estimated_cost)}
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCompleteTask(task.id)}
                          className="h-7 px-2"
                        >
                          <CheckCircle className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTask(task.id)}
                          className="h-7 px-2 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Tasks */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Anstehende Wartungen ({upcomingTasks.length})
            </h4>
            <Button 
              size="sm" 
              variant="outline" 
              className="flex items-center gap-1 bg-gradient-to-r from-blue-50 to-slate-100 dark:from-blue-900/20 dark:to-slate-800 border-slate-300 dark:border-slate-600" 
              onClick={onCreateMaintenance}
            >
              <Plus className="h-3 w-3" />
              Neue Wartung
            </Button>
          </div>
          
          {upcomingTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Keine anstehenden Wartungen</p>
              <p className="text-sm">
                {selectedOwner !== 'all' || selectedProperty !== 'all' 
                  ? 'Für die aktuelle Auswahl sind alle Wartungen auf dem neuesten Stand!'
                  : 'Alle Wartungen sind auf dem neuesten Stand!'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingTasks.map((task) => (
                <div key={task.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gradient-to-r from-slate-50 via-blue-50/30 to-slate-50 dark:from-slate-800/50 dark:via-blue-900/10 dark:to-slate-800/50 hover:shadow-md transition-all duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getPriorityColor(task.priority)}>
                          {formatPriority(task.priority)}
                        </Badge>
                        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                          {getRelativeDate(task.scheduled_date)}
                        </Badge>
                      </div>
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        {task.title || task.description || 'Wartungsaufgabe'}
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {task.address}
                        {task.units && ` • Einheit ${task.unit_info?.unit_identifier}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {task.contractor_name || 'Kein Auftragnehmer'} • {task.maintenance_type || 'Allgemeine Wartung'}
                      </p>
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatCurrency(task.estimated_cost)}
                      </p>
                      <div className="flex items-center gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCompleteTask(task.id)}
                          className="h-7 px-2"
                        >
                          <CheckCircle className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTask(task.id)}
                          className="h-7 px-2 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {maintenanceTasks.length > 5 && (
                <div className="text-center pt-3">
                  <Button variant="outline" size="sm" className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20">
                    Alle {maintenanceTasks.length} Wartungen anzeigen
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MaintenanceCalendarWidget;

